import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, Save } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import { MaquinaForm, MaquinaFormData } from './maquina-form';

const dadosVazios: MaquinaFormData = {
  equipamento: '',
  tipo: '',
  area: '',
  local: '',
  tag: '',
  item: '',
  idItem: '',
  status: '',
  funcionario: '',
  observacao: '',
  refSap: '',
  refSismanaAntigo: '',
  refSismanaNovo: '',
  numeroAmt: '',
  numeroAprPraxis: '',
  template: '',
};

export function MaquinaEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [dados, setDados] = useState<MaquinaFormData>(dadosVazios);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) carregarMaquina();
  }, [id]);

  const carregarMaquina = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await apiService.buscarAtivo(id);
      if (response.success && response.data) {
        const a = response.data;
        setDados({
          equipamento: a.equipamento || '',
          tipo: a.tipo || '',
          area: a.area || '',
          local: a.local || '',
          tag: a.tag || '',
          item: a.item || '',
          idItem: a.idItem || '',
          status: a.status || '',
          funcionario: a.funcionario || '',
          observacao: a.observacao || '',
          refSap: a.refSap || '',
          refSismanaAntigo: a.refSismanaAntigo || '',
          refSismanaNovo: a.refSismanaNovo || '',
          numeroAmt: a.numeroAmt || '',
          numeroAprPraxis: a.numeroAprPraxis || '',
          template: a.template || '',
        });
      } else {
        setErrors({ _geral: t('maquinas.form.erro.naoEncontrada') });
      }
    } catch (error) {
      console.error('Erro ao carregar máquina:', error);
      setErrors({ _geral: t('maquinas.form.erro.carregar') });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (campo: keyof MaquinaFormData, valor: string) => {
    setDados((prev) => ({ ...prev, [campo]: valor }));
    if (errors[campo]) {
      setErrors((prev) => { const next = { ...prev }; delete next[campo]; return next; });
    }
  };

  const validar = (): boolean => {
    const novosErrors: Record<string, string> = {};
    if (!dados.equipamento.trim()) novosErrors.equipamento = t('maquinas.form.erro.equipamentoObrigatorio');
    setErrors(novosErrors);
    return Object.keys(novosErrors).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar() || !id) return;

    setSalvando(true);
    try {
      const payload: any = {};
      (Object.keys(dados) as (keyof MaquinaFormData)[]).forEach((campo) => {
        payload[campo] = dados[campo] || null;
      });

      const response = await apiService.atualizarAtivo(id, payload);

      if (response.success) {
        navigate('/maquinas');
      } else {
        setErrors({ _geral: response.message || t('maquinas.form.erro.salvar') });
      }
    } catch (error) {
      console.error('Erro ao atualizar máquina:', error);
      setErrors({ _geral: t('maquinas.form.erro.salvarTentarNovamente') });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue mx-auto mb-3" />
          <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light text-sm">{t('comum.estado.carregando')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link
              to="/maquinas"
              className="text-schmersal-gray-medium dark:text-schmersal-gray-light hover:text-schmersal-blue dark:hover:text-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">
              {t('maquinas.form.tituloEditar')}
            </h1>
          </div>
          <p className="ml-7 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {dados.equipamento || t('comum.estado.carregando')}
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/maquinas"
            className="inline-flex items-center justify-center px-4 py-2.5 border border-rg-line dark:border-schmersal-gray-dark rounded-lg
              text-sm font-semibold text-schmersal-gray-dark dark:text-white bg-white dark:bg-schmersal-gray-darker
              hover:border-schmersal-blue hover:text-schmersal-blue focus:outline-none focus:ring-2 focus:ring-schmersal-blue transition-all duration-200"
          >
            {t('comum.acao.cancelar')}
          </Link>
          <button
            onClick={handleSalvar}
            disabled={salvando}
            className="inline-flex items-center justify-center px-5 py-2.5 border-2 border-transparent rounded-lg
              shadow-md text-sm font-semibold text-white bg-schmersal-blue hover:bg-schmersal-blue-dark
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue
              disabled:opacity-50 transition-all duration-200"
          >
            <Save className="h-4 w-4 mr-2" />
            {salvando ? t('comum.acao.salvando') : t('maquinas.form.salvarAlteracoes')}
          </button>
        </div>
      </div>

      {errors._geral && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-sm font-medium">
          {errors._geral}
        </div>
      )}

      <MaquinaForm dados={dados} onChange={handleChange} errors={errors} />
    </div>
  );
}
